// resources/js/components/bid_limits/BidLimitsPopover.tsx

import * as React from 'react'
import { useState } from 'react'
import { X } from 'lucide-react'
import { Popover } from '../ui/popover'
import { Button } from '../ui/button'
import { Tooltip } from '../ui/tooltip'
import { Toasts } from '../../packages/blueprint-components/src/Toaster'
import { cents } from '../../util/formatters'
import { ResolvedBidLimit, ResolvedBidLimits } from '../../types/bid_limits'
import { clearBidLimits, saveBidLimits } from './api'

const SOURCE_LABEL: Record<string, string> = {
    ad_group: 'ad group',
    campaign: 'campaign',
    profile: 'profile',
}

/** cents (int) -> editable dollar string */
const toDollars = (value: number | null): string => (value == null ? '' : (value / 100).toFixed(2))

/** dollar string -> cents (int), null when blank, NaN when unparseable */
const toCents = (raw: string): number | null => {
    const trimmed = raw.trim()
    if (trimmed === '') {
        return null
    }
    const n = Number(trimmed)
    return Number.isFinite(n) ? Math.round(n * 100) : NaN
}

const inheritedHint = (side: ResolvedBidLimit, kind: string): React.ReactNode => {
    if (side.source === 'self') {
        return null
    }
    if (side.source === 'none' || side.value == null) {
        return `No ${kind} set`
    }
    return (
        <Tooltip content={`Inherited from ${SOURCE_LABEL[side.source] ?? side.source}`}>
            <span>Inherited {cents(side.value)}</span>
        </Tooltip>
    )
}

interface FormProps {
    parentType: number
    parentId: string
    limits: ResolvedBidLimits
    onSaved: (next: ResolvedBidLimits) => void
    onClose: () => void
}

export const BidLimitsForm = ({ parentType, parentId, limits, onSaved, onClose }: FormProps) => {
    const [minStr, setMinStr] = useState(
        limits.min.source === 'self' ? toDollars(limits.min.value) : '',
    )
    const [maxStr, setMaxStr] = useState(
        limits.max.source === 'self' ? toDollars(limits.max.value) : '',
    )
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const hasOwn = limits.min.source === 'self' || limits.max.source === 'self'

    const minCents = toCents(minStr)
    const maxCents = toCents(maxStr)
    const bothBlank = minCents === null && maxCents === null
    const hasChanges =
        minStr !== (limits.min.source === 'self' ? toDollars(limits.min.value) : '') ||
        maxStr !== (limits.max.source === 'self' ? toDollars(limits.max.value) : '')

    const validate = (): string | null => {
        if (Number.isNaN(minCents) || Number.isNaN(maxCents)) {
            return 'Enter a valid dollar amount.'
        }
        if (minCents !== null && minCents < 1) {
            return 'Minimum bid must be at least $0.01.'
        }
        if (maxCents !== null && maxCents < 2) {
            return 'Maximum bid must be at least $0.02.'
        }
        if (minCents !== null && maxCents !== null && minCents > maxCents) {
            return 'Minimum bid must be less than or equal to the maximum bid.'
        }
        return null
    }

    const validationError = validate()
    const canSave = !saving && validationError === null && (!bothBlank || (hasOwn && hasChanges))

    const save = async () => {
        if (!canSave) {
            setError(bothBlank ? null : validationError)
            return
        }
        setError(null)
        setSaving(true)
        try {
            const next = bothBlank
                ? await clearBidLimits(parentType, parentId)
                : await saveBidLimits(parentType, parentId, minCents, maxCents)
            onSaved(next)
            Toasts.success(bothBlank ? 'Bid limits reset to inherited.' : 'Bid limits updated.')
            onClose()
        } catch (e: any) {
            setError(e?.response?.data?.message ?? 'Could not save bid limits. Please try again.')
        } finally {
            setSaving(false)
        }
    }

    const reset = async () => {
        setError(null)
        setSaving(true)
        try {
            const next = await clearBidLimits(parentType, parentId)
            onSaved(next)
            Toasts.success('Bid limits reset to inherited.')
            onClose()
        } catch (e: any) {
            setError(e?.response?.data?.message ?? 'Could not reset bid limits. Please try again.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div
            className="w-[340px] px-3 py-3"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                if (e.key === 'Escape') {
                    e.preventDefault()
                    onClose()
                }
            }}
        >
            <div className="mb-4 text-base font-semibold text-gray-900">Bid Limits</div>

            <div className="flex gap-4">
                <MoneyField
                    label="Minimum"
                    value={minStr}
                    onChange={setMinStr}
                    onReset={() => setMinStr('')}
                    onCommit={save}
                    disabled={saving}
                    hint={inheritedHint(limits.min, 'minimum')}
                />
                <MoneyField
                    label="Maximum"
                    value={maxStr}
                    onChange={setMaxStr}
                    onReset={() => setMaxStr('')}
                    onCommit={save}
                    disabled={saving}
                    hint={inheritedHint(limits.max, 'maximum')}
                />
            </div>

            {(error ?? validationError) && (
                <div className="mt-2 text-xs text-red-600">{error ?? validationError}</div>
            )}

            <div className="mt-3 flex items-center justify-between">
                <div>
                    {hasOwn && (
                        <Button
                            minimal
                            small
                            text="Reset to inherited"
                            className="text-gray-400 hover:text-gray-600"
                            onClick={reset}
                            disabled={saving}
                        />
                    )}
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" text="Cancel" onClick={onClose} disabled={saving} />
                    <Button
                        variant="default"
                        text="Save"
                        onClick={save}
                        loading={saving}
                        disabled={!canSave}
                    />
                </div>
            </div>
        </div>
    )
}

interface MoneyFieldProps {
    label: string
    value: string
    onChange: (v: string) => void
    onReset: () => void
    onCommit: () => void
    disabled: boolean
    hint: React.ReactNode
}

const MoneyField = ({
    label,
    value,
    onChange,
    onReset,
    onCommit,
    disabled,
    hint,
}: MoneyFieldProps) => {
    const inputId = React.useId()

    return (
        <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-gray-700" htmlFor={inputId}>
                {label}
            </label>
            <span className="flex items-center rounded border border-gray-300 px-2 focus-within:border-blue-500">
                <span className="text-sm text-gray-400">$</span>
                <input
                    id={inputId}
                    type="text"
                    inputMode="decimal"
                    aria-label={label}
                    className="w-full border-0 py-1 pl-1 pr-1 text-sm outline-none focus:ring-0"
                    value={value}
                    disabled={disabled}
                    placeholder="—"
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            onCommit()
                        }
                    }}
                />
                <button
                    type="button"
                    aria-label={`Reset ${label.toLowerCase()} to inherited`}
                    className="ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:pointer-events-none disabled:opacity-40"
                    disabled={disabled || value === ''}
                    onClick={onReset}
                >
                    <X size={14} strokeWidth={2} />
                </button>
            </span>
            {hint && <span className="mt-1 block text-[11px] text-gray-400">{hint}</span>}
        </div>
    )
}

interface PopoverProps {
    parentType: number
    parentId: string
    limits: ResolvedBidLimits
    onSaved: (next: ResolvedBidLimits) => void
    children: React.ReactNode
}

/**
 * Click-to-open editor for an entity's bid limits. Rendered via the shared
 * `ui/popover`, which portals to document.body so the form is never clipped
 * by the AgGrid cell.
 */
export const BidLimitsPopover = ({
    parentType,
    parentId,
    limits,
    onSaved,
    children,
}: PopoverProps) => {
    const [open, setOpen] = useState(false)

    return (
        <Popover
            interactionKind="click"
            isOpen={open}
            onOpened={() => setOpen(true)}
            onClose={() => setOpen(false)}
            position="bottom-start"
            content={
                <BidLimitsForm
                    parentType={parentType}
                    parentId={parentId}
                    limits={limits}
                    onSaved={onSaved}
                    onClose={() => setOpen(false)}
                />
            }
        >
            {children}
        </Popover>
    )
}
